import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { ComboBox, type ComboBoxOption, type ComboBoxProps } from './ComboBox';

interface InfiniteQueryResult<TData> {
  data?: { pages: TData[] };
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export interface InfiniteComboBoxProps<TParams, TData> extends Omit<
  ComboBoxProps,
  'options'
> {
  /** A `useInfiniteQuery`-based hook that fetches one page at a time. Must return the standard TanStack infinite query shape. */
  useHook: (params: TParams) => InfiniteQueryResult<TData>;
  /** Base query params passed to the hook on every call. Omit `page` — the hook manages pagination internally. */
  baseParams?: Omit<TParams, 'page'>;
  /** Maps a single page of API data to ComboBox options. Called once per page; results are flat-mapped across all pages. */
  mapOptions: (pageData: TData) => ComboBoxOption[];
  /** Pre-selected options to show even when they aren't in the current page results (e.g. the current saved value). */
  seedOptions?: ComboBoxOption[];
  /** The query param key used for search. Defaults to 'name'. Override if the API uses a different key (e.g. 'search', 'query'). */
  searchParamKey?: string;
  onAddNew?: () => void;
  /** Called alongside onChange in multi-select mode, with the full ComboBoxOption objects for each selected value. */
  onChangeOption?: (options: ComboBoxOption[]) => void;
}

/**
 * A paginated async combobox backed by a `useInfiniteQuery` hook.
 *
 * Pass a hook that uses `useInfiniteQuery` as `useHook` — the component handles
 * debounced search, page accumulation, and scroll-triggered load-more automatically.
 *
 * Intended as the long-term replacement for `AsyncComboBox`. Migrate a field by
 * creating a `useInfinite*` hook and swapping `AsyncComboBoxField` → `InfiniteComboBoxField`.
 */
export const InfiniteComboBox = <TParams extends object | undefined, TData>({
  useHook,
  mapOptions,
  baseParams,
  onChange,
  onChangeOption,
  none = true,
  seedOptions = [],
  searchParamKey = 'name',
  ...props
}: InfiniteComboBoxProps<TParams, TData>) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const params = {
    ...(baseParams as TParams),
    ...(debouncedSearch ? { [searchParamKey]: debouncedSearch } : {}),
  } as TParams;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useHook(params);

  const options = useMemo(
    () => data?.pages.flatMap(mapOptions) ?? [],
    [data?.pages, mapOptions]
  );

  // A search narrows the loaded list: keep every chosen option listed so its
  // label stays on the field.
  const seen = useRef(new Map<ComboBoxOption['value'], ComboBoxOption>());
  options.forEach((opt) => seen.current.set(opt.value, opt));
  const value = props.value;
  const optionsWithSeeds = useMemo(() => {
    const chosen = ([] as unknown[])
      .concat(value ?? [])
      .flatMap((v) => seen.current.get(v as string) ?? []);
    const extra = [...seedOptions, ...chosen];
    return [
      ...options,
      ...extra.filter(
        (s, i) =>
          !options.some((o) => o.value === s.value) &&
          extra.findIndex((e) => e.value === s.value) === i
      ),
    ];
  }, [options, seedOptions, value]);

  const handleChange = useCallback(
    (raw: string[] | string) => {
      onChange?.(raw as never);
      const values = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const selected = values
        .map((v) => optionsWithSeeds.find((opt) => opt.value === v))
        .filter(Boolean) as ComboBoxOption[];
      onChangeOption?.(selected);
    },
    [onChange, onChangeOption, optionsWithSeeds]
  );

  const comboBoxProps = {
    ...props,
    onChange: onChangeOption ? handleChange : onChange,
    none,
    options: optionsWithSeeds,
    hasMore: hasNextPage ?? false,
    isLoadingMore: isFetchingNextPage,
    onLoadMore: () => {
      if (!isFetchingNextPage) fetchNextPage();
    },
    onSearch: setSearch,
  } as ComboBoxProps;

  return <ComboBox {...comboBoxProps} />;
};
